import { useApolloClient } from '@apollo/client';
import { miniButtonDockScrollBottomPad, translateTemplateText } from '@focuslee/shared-mini-app';
import { Stack } from '@mui/material';
import { List, Text } from '@telegram-apps/telegram-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { GetQuestsQuery, GetQuestsQueryVariables, QuestDifficulty, QuestMetricType } from '../../client/generated/graphql';
import { getQuestsQuery } from '../../client/queries/quest';
import { ListCategory, ListQuest } from '../../client/types';
import { getConditionIcon } from '../../helpers/achievement';
import { getButtonThemeColors } from '../../helpers/theme';
import { useCachedCategoriesWithFallback } from '../../hooks/caching/category';
import { useCachedQuestsWithFallback } from '../../hooks/caching/quest';
import { Page } from '../Page';
import { CommonIcon } from './CommonIcon';
import { ConditionPickerViewListFooter } from './ConditionPickerViewListFooter';
import { ConditionQuestBlock, type ConditionQuestForBlock } from './ConditionQuestBlock';
import { CustomInput } from './CustomInput';
import { CustomSelect } from './CustomSelect';
import { FocusLeePlaceholderImage } from './FocusLeePlaceholderImage';
import { SearchableButtonDock } from './SearchableButtonDock';

const QUESTS_PAGE_SIZE = 10;

const voicePlaceholderConditionQuest = (name: string): ConditionQuestForBlock => ({
    name,
    timing: false,
    timedInterval: null,
    timerValueInMs: null,
    difficulty: QuestDifficulty.NoEffort,
    metric: {
        __typename: 'QuestMetric',
        completed: false,
        xpPoints: 0,
        completedTimes: 0,
        timeSpentSec: 0,
    },
    repeat: null,
});

export type VoiceLinkedQuestPickerItem = {
    voiceActionId: string;
    name: string;
    categoryId?: string;
    categoryName?: string;
};

interface ConditionPickerViewProps {
    initialMetricType: QuestMetricType;
    initialTarget: number | null;
    initialQuestIds: string[];
    voiceLinkedQuests?: VoiceLinkedQuestPickerItem[];
    initialSelectedVoiceActionIds?: string[];
    onSave: (metricType: QuestMetricType, target: number | null, questIds: string[], includedVoiceActionIds?: string[]) => void;
    onCancel: () => void;
}

export const ConditionPickerView: FC<ConditionPickerViewProps> = ({
    initialMetricType,
    initialTarget,
    initialQuestIds,
    voiceLinkedQuests,
    initialSelectedVoiceActionIds = [],
    onSave,
    onCancel,
}) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const { categories } = useCachedCategoriesWithFallback();
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastFetchedQuestId, setLastFetchedQuestId] = useState<string | null>(null);
    const [allQuests, setAllQuests] = useState<ListQuest[]>([]);

    const { quests: selectedQuests } = useCachedQuestsWithFallback(initialQuestIds);

    useEffect(() => {
        setIsFetchingMore(true);

        client
            .query<GetQuestsQuery, GetQuestsQueryVariables>({
                query: getQuestsQuery,
                variables: {
                    limit: QUESTS_PAGE_SIZE,
                    startAtId: undefined,
                },
            })
            .then(result => {
                if (result.data?.quests) {
                    setAllQuests(result.data.quests);

                    if (result.data.quests.length > 0) {
                        const lastQuest = result.data.quests[result.data.quests.length - 1];
                        setLastFetchedQuestId(lastQuest.id);
                    }
                }
                setHasMore(result.data?.quests?.length === QUESTS_PAGE_SIZE || false);
                setIsFetchingMore(false);
            })
            .catch(() => {
                setIsFetchingMore(false);
                setHasMore(false);
            });
    }, [client]);

    const [metricType, setMetricType] = useState<QuestMetricType>(initialMetricType);
    const [target, setTarget] = useState<number | null>(initialTarget);
    const [selectedQuestIds, setSelectedQuestIds] = useState<Set<string>>(new Set(initialQuestIds));
    const hasVoiceLinkedQuests = Boolean(voiceLinkedQuests?.length);
    const [selectedVoiceActionIds, setSelectedVoiceActionIds] = useState(() => new Set(initialSelectedVoiceActionIds));
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchOpen, setSearchOpen] = useState(false);
    const initialSelectedIds = useRef<Set<string>>(new Set(initialQuestIds));

    // Create maps for quick lookup
    const categoryMap = useMemo(
        () =>
            categories.reduce(
                (map: Record<string, ListCategory>, cat: ListCategory) => ({ ...map, [cat.id]: cat }),
                {} as Record<string, ListCategory>,
            ),
        [categories],
    );

    const resolveVoiceQuestCategory = useCallback(
        (vq: VoiceLinkedQuestPickerItem): ListCategory | null => {
            if (vq.categoryId && categoryMap[vq.categoryId]) return categoryMap[vq.categoryId];
            if (vq.categoryName?.trim()) {
                const n = vq.categoryName.trim().toLowerCase();
                return categories.find(c => c.name.toLowerCase() === n) ?? null;
            }
            return null;
        },
        [categories, categoryMap],
    );

    // Metric type display mapping
    const getMetricTypeDisplay = (type: QuestMetricType) => {
        switch (type) {
            case QuestMetricType.Experience:
                return {
                    name: t('conditionPicker.experience'),
                    description: t('conditionPicker.experienceDescription'),
                };
            case QuestMetricType.Completion:
                return {
                    name: t('conditionPicker.completion'),
                    description: t('conditionPicker.completionDescription'),
                };
            case QuestMetricType.Time:
                return {
                    name: t('conditionPicker.time'),
                    description: t('conditionPicker.timeDescription'),
                };
            case QuestMetricType.Repeating:
                return {
                    name: t('conditionPicker.repeat'),
                    description: t('conditionPicker.repeatDescription'),
                };
            default:
                return {
                    name: t('conditionPicker.unknown'),
                    description: t('conditionPicker.unknownDescription'),
                };
        }
    };

    // Metric type options
    const metricTypeOptions = useMemo(() => {
        return Object.values(QuestMetricType).map(type => {
            const display = getMetricTypeDisplay(type);
            return {
                id: type,
                label: display.name,
                value: type,
                icon: getConditionIcon(type),
                description: display.description,
            };
        });
    }, [t]);

    const filteredQuests = useMemo(() => {
        const allAvailableQuests = [...selectedQuests, ...allQuests];

        const uniqueQuests = Array.from(new Map(allAvailableQuests.map(q => [q.id, q])).values());

        const query = searchQuery.toLowerCase();
        const filtered = query
            ? uniqueQuests.filter(quest => translateTemplateText(t, quest.templateId, 'name', quest.name).toLowerCase().includes(query))
            : uniqueQuests;

        return filtered.sort((a, b) => {
            const aIsInitiallySelected = initialSelectedIds.current.has(a.id);
            const bIsInitiallySelected = initialSelectedIds.current.has(b.id);

            if (aIsInitiallySelected && !bIsInitiallySelected) return -1;
            if (!aIsInitiallySelected && bIsInitiallySelected) return 1;
            return 0;
        });
    }, [selectedQuests, allQuests, searchQuery, t]);

    const fetchMoreQuests = useCallback(() => {
        if (isFetchingMore || !hasMore || !lastFetchedQuestId) return;

        setIsFetchingMore(true);

        const limit = searchQuery ? undefined : QUESTS_PAGE_SIZE;

        client
            .query<GetQuestsQuery, GetQuestsQueryVariables>({
                query: getQuestsQuery,
                variables: {
                    limit,
                    startAtId: lastFetchedQuestId,
                },
            })
            .then(result => {
                if (result.data?.quests) {
                    setAllQuests(prev => [...prev, ...result.data!.quests]);

                    if (result.data.quests.length > 0) {
                        const lastQuest = result.data.quests[result.data.quests.length - 1];
                        setLastFetchedQuestId(lastQuest.id);
                    }
                }
                setHasMore(!searchQuery && result.data?.quests?.length === QUESTS_PAGE_SIZE);

                setIsFetchingMore(false);
            })
            .catch(() => {
                setIsFetchingMore(false);
                setHasMore(false);
            });
    }, [isFetchingMore, hasMore, lastFetchedQuestId, searchQuery, client]);

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            const scrollBottom = scrollHeight - scrollTop - clientHeight;

            if (scrollBottom < 50) {
                fetchMoreQuests();
            }
        },
        [fetchMoreQuests],
    );

    useEffect(() => {
        if (hasMore && !isFetchingMore && lastFetchedQuestId && filteredQuests.length < 10) fetchMoreQuests();
    }, [hasMore, isFetchingMore, lastFetchedQuestId, filteredQuests.length, fetchMoreQuests]);

    const totalSelectedQuestCount = selectedVoiceActionIds.size + selectedQuestIds.size;
    const isValid = (metricType === QuestMetricType.Completion || (target !== null && target > 0)) && totalSelectedQuestCount > 0;

    const handleSave = useCallback(() => {
        if (!isValid) return;

        const targetValue = metricType === QuestMetricType.Completion ? null : target;
        onSave(
            metricType,
            targetValue,
            Array.from(selectedQuestIds),
            hasVoiceLinkedQuests ? Array.from(selectedVoiceActionIds) : undefined,
        );
    }, [isValid, metricType, target, selectedQuestIds, selectedVoiceActionIds, hasVoiceLinkedQuests, onSave]);

    const handleQuestToggle = useCallback((questId: string) => {
        setSelectedQuestIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(questId)) {
                newSet.delete(questId);
            } else {
                newSet.add(questId);
            }
            return newSet;
        });
    }, []);

    const handleVoiceLinkedToggle = useCallback((voiceActionId: string) => {
        setSelectedVoiceActionIds(prev => {
            const next = new Set(prev);
            if (next.has(voiceActionId)) next.delete(voiceActionId);
            else next.add(voiceActionId);
            return next;
        });
    }, []);

    return (
        <Page
            back
            insetEnabled={false}
            backClick={onCancel}
            footer={
                <SearchableButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: onCancel, navigation: 'back' }}
                    main={{
                        text: t('actions.save'),
                        onClick: handleSave,
                        enabled: isValid,
                        bgColor: getButtonThemeColors(isValid).backgroundColor,
                    }}
                    searchOpen={searchOpen}
                    searchQuery={searchQuery}
                    searchPlaceholder={t('category.searchPlaceholder')}
                    searchAriaLabel={t('category.search')}
                    closeSearchAriaLabel={t('picker.closeSearch')}
                    onSearchQueryChange={setSearchQuery}
                    onOpenSearch={() => setSearchOpen(true)}
                    onCloseSearch={() => {
                        setSearchOpen(false);
                        setSearchQuery('');
                    }}
                />
            }
        >
            <Stack
                paddingTop="calc(var(--tg-viewport-content-safe-area-inset-top) + 20px)"
                paddingLeft="16px"
                paddingRight="16px"
                rowGap="20px"
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                    <CustomSelect
                        selectedId={metricType}
                        setSelectedId={value => setMetricType(value as QuestMetricType)}
                        options={metricTypeOptions}
                        before={option => (
                            <CommonIcon
                                style={{ marginRight: '8px' }}
                                icon={option.icon}
                                type="filled"
                                color="var(--tg-theme-text-color)"
                                size={20}
                            />
                        )}
                        text={option => (
                            <Stack direction="column" gap={0.2}>
                                <Text weight="2">{option.label}</Text>
                                <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>{option.description}</Text>
                            </Stack>
                        )}
                        renderSelected={option => (option ? option.label : t('conditionPicker.selectType'))}
                        renderSelectedBefore={option => (
                            <CommonIcon icon={option ? option.icon : 'rule'} type="filled" color="var(--tg-theme-text-color)" size={20} />
                        )}
                    />
                    {metricType !== QuestMetricType.Completion && (
                        <Stack direction="row" gap={1} sx={{ minWidth: '100px' }}>
                            <Text weight="2">{t('conditionPicker.target')}</Text>
                            <CustomInput
                                inputMode="numeric"
                                value={target?.toString()}
                                onFocus={e => e.target.select()}
                                onChange={e => setTarget(Math.max(1, parseInt(e.target.value) || 1))}
                                placeholder={t('conditionPicker.target')}
                                wrapperStyle={{ minHeight: '0px', maxWidth: '40px' }}
                            />
                        </Stack>
                    )}
                </Stack>

                <Stack direction="column" gap={1}>
                    {totalSelectedQuestCount > 0 ? (
                        <Text style={{ fontSize: '12px', color: 'var(--tg-theme-button-color)' }}>
                            {t('conditionPicker.selected', { count: totalSelectedQuestCount })}
                        </Text>
                    ) : (
                        <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>
                            {t('conditionPicker.chooseQuest')}
                        </Text>
                    )}
                </Stack>
            </Stack>
            <List
                style={{
                    flex: 1,
                    overflowY: 'scroll',
                    paddingBottom: miniButtonDockScrollBottomPad,
                }}
                onScroll={handleScroll}
            >
                {voiceLinkedQuests?.map(vq => {
                    const cat = resolveVoiceQuestCategory(vq);
                    if (!cat) return null;
                    return (
                        <ConditionQuestBlock
                            key={vq.voiceActionId}
                            quest={voicePlaceholderConditionQuest(vq.name)}
                            category={cat}
                            metricType={metricType}
                            isSelected={selectedVoiceActionIds.has(vq.voiceActionId)}
                            onToggle={() => handleVoiceLinkedToggle(vq.voiceActionId)}
                        />
                    );
                })}
                {filteredQuests.map(quest => {
                    const category = categoryMap[quest.categoryId];

                    if (!category) return null;

                    return (
                        <ConditionQuestBlock
                            key={quest.id}
                            quest={quest}
                            category={category}
                            metricType={metricType}
                            isSelected={selectedQuestIds.has(quest.id)}
                            onToggle={() => handleQuestToggle(quest.id)}
                        />
                    );
                })}

                <ConditionPickerViewListFooter
                    hasMore={hasMore}
                    isFetchingMore={isFetchingMore}
                    filteredQuestsLength={filteredQuests.length}
                />

                {filteredQuests.length === 0 && !isFetchingMore && (
                    <Stack alignItems="center" spacing={1} sx={{ padding: '20px', color: 'var(--tg-theme-hint-color)' }}>
                        <FocusLeePlaceholderImage kind="conditionsSearch" size={112} />
                        <Text style={{ fontSize: '14px' }}>{searchQuery ? t('conditionPicker.noQuestsFound') : t('conditionPicker.noQuestsAvailable')}</Text>
                    </Stack>
                )}
            </List>
        </Page>
    );
};
